import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Empty, Popconfirm, Table, Tag, Tooltip, App as AntdApp } from 'antd';

import { clearViews, fetchRecentViews, removeView } from '../services/views';
import useResolvedViewHistory from '../hooks/useResolvedViewHistory';
import useDateFormat from '../hooks/useDateFormat';
import { formatDisplayDateTime } from '../utils/dateDisplay';
import RecordBadge from '../components/common/RecordBadge';
import ContextBar from '../components/common/ContextBar';
import { CARD_ICON, CLOSE, COLLECTION_ICON } from '../components/icons';
import { destructivePopconfirmProps } from '../components/common/confirmDestructive';
import { DataState, Skeleton, deriveDataState } from '../components/loading';

const KIND_LABELS = { collection: 'Collection', card: 'Card', item: 'Item' };

/**
 * Everything the user has viewed, newest first — the "view all history" page
 * behind the command palette's recents. The server keeps one row per
 * (user, target) and already dropped anything the user can no longer read.
 */
export default function History() {
	const navigate = useNavigate();
	const { message } = AntdApp.useApp();
	const [views, setViews] = useState(null);
	const [loadError, setLoadError] = useState(null);
	const [loading, setLoading] = useState(true);
	const requestRef = useRef(0);
	const [clearing, setClearing] = useState(false);
	useDateFormat();

	// A re-read keeps the lines on screen; a failed one says so above them.
	const load = useCallback(() => {
		const request = ++requestRef.current;
		setLoading(true);
		setLoadError(null);
		fetchRecentViews(200)
			.then(list => { if (request === requestRef.current) { setViews(list); setLoading(false); } })
			.catch(error => { if (request === requestRef.current) { setLoadError(error); setLoading(false); } });
	}, []);

	useEffect(() => {
		load();
		return () => { requestRef.current += 1; };
	}, [load]);

	const rows = useResolvedViewHistory(views);

	// Forgetting one line: optimistic, since the server call is idempotent and
	// a failure simply puts the row back on the next load.
	const handleRemove = async (row) => {
		const before = views;
		setViews(current => (current || []).filter(
			view => !(view.targetType === row.targetType && String(view.target_id) === String(row.id))
		));
		try {
			await removeView(row.targetType, row.id);
		} catch {
			setViews(before);
			message.error('Removing that entry failed. Please try again.');
		}
	};

	const handleClear = async () => {
		setClearing(true);
		try {
			await clearViews();
			setViews([]);
			message.success('Recently viewed cleared.');
		} catch {
			message.error('Clearing history failed. Please try again.');
		} finally {
			setClearing(false);
		}
	};

	const columns = [
		{
			title: 'Title',
			dataIndex: 'title',
			key: 'title',
			render: (title, row) => (
				<button
					type="button"
					className="history-page__link"
					onClick={() => navigate(row.path)}
				>
					{row.card ? (
						<RecordBadge
							kind={row.targetType}
							record={row.card}
							kicker={row.targetType === 'item' ? row.card?.name : undefined}
							title={title}
							size="xs"
							collection={row.owner}
						/>
					) : (
						<>
							<span className="history-page__row-icon">
								{row.targetType === 'card' ? <CARD_ICON /> : <COLLECTION_ICON />}
							</span>
							<span className="history-page__title">{title}</span>
						</>
					)}
				</button>
			),
		},
		{
			title: 'Type',
			dataIndex: 'targetType',
			key: 'targetType',
			width: 120,
			render: type => <Tag>{KIND_LABELS[type] || type}</Tag>,
		},
		{
			title: 'Viewed',
			dataIndex: 'viewedAt',
			key: 'viewedAt',
			width: 200,
			render: value => (value ? formatDisplayDateTime(value) : '—'),
		},
		{
			title: '',
			key: 'forget',
			width: 56,
			align: 'right',
			render: (_value, row) => (
				<Tooltip title="Remove from history">
					<Button
						type="text"
						size="small"
						className="history-page__forget"
						icon={<CLOSE aria-hidden="true" />}
						onClick={() => handleRemove(row)}
						aria-label={`Remove ${row.title} from history`}
					/>
				</Tooltip>
			),
		},
	];

	return (
		<>
			<ContextBar
				title="Recently viewed"
				actions={rows.length ? (
					<Popconfirm
						title="Clear your entire view history?"
						{...destructivePopconfirmProps('Clear')}
						onConfirm={handleClear}
					>
						<Button danger loading={clearing}>
							Clear history
						</Button>
					</Popconfirm>
				) : null}
			/>

			<DataState
				state={deriveDataState({
					loading,
					error: loadError,
					hasData: views !== null,
					isEmpty: views !== null && rows.length === 0,
				})}
				hasData={views !== null}
				isEmpty={views !== null && rows.length === 0}
				skeleton={<Skeleton variant="list-row" count={6} />}
				empty={<Empty description="Nothing viewed yet — collections, cards, and items you open will show up here." />}
				error={loadError ? 'Check your connection and try again.' : null}
				onRetry={load}
				label="your history"
			>
				<Table
					rowKey="key"
					columns={columns}
					dataSource={rows}
					pagination={rows.length > 50 ? { pageSize: 50, showSizeChanger: false } : false}
					size="middle"
				/>
			</DataState>
		</>
	);
}
